import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../services/api';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { usePopup } from '../context/PopupContext';
import { timeAgo } from '../utils/time';
import './Feedback.css';

export const CATEGORIES = [
  { key: 'bug', label: 'Bug', icon: 'warning', hint: 'Something is broken or looks wrong', placeholder: 'What happened, and what did you expect instead?' },
  { key: 'suggestion', label: 'Suggestion', icon: 'tip', hint: 'An idea to make Zaptude better', placeholder: 'What would you like Zaptude to do?' },
  { key: 'question', label: 'Question', icon: 'info', hint: 'Something you need help with', placeholder: 'What would you like to know?' },
  { key: 'general', label: 'General', icon: 'feedback', hint: 'Anything else', placeholder: 'Tell us what\'s on your mind.' },
];
const STATUS = { new: { label: 'Received', cls: 'z-chip--cyan' }, in_review: { label: 'In review', cls: 'z-chip--warning' }, resolved: { label: 'Resolved', cls: 'z-chip--success' } };
const MAX = 2000;

/**
 * Feedback for every kind of user (students, college admins, super admins). Simple on purpose: a category and a
 * message. It goes to the Zaptude product team's inbox (server: POST /api/feedback); the sender can see the
 * status of what they sent — never the team's internal notes.
 * (Screenshot attachments are not offered: the backend has no safe file-upload store yet.)
 */
export default function Feedback() {
  const { showToast } = usePopup();
  const location = useLocation();
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState('');
  const [mine, setMine] = useState(null);
  const [mineError, setMineError] = useState(null);

  const loadMine = useCallback(async () => {
    setMineError(null);
    try { setMine((await API.get('/feedback/mine')).data); } catch (err) { setMineError(err); }
  }, []);
  useEffect(() => { loadMine(); }, [loadMine]);

  const cat = CATEGORIES.find(c => c.key === category);
  const trimmed = message.trim();
  const valid = trimmed.length >= 3 && trimmed.length <= MAX;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true); setProblem('');
    try {
      await API.post('/feedback', { category, message: trimmed, page: location.state?.from });
      setMessage('');
      showToast('Thanks — your feedback reached the Zaptude team.', { tone: 'success' });
      loadMine();
    } catch (err) {
      setProblem(err.response?.data?.message || (err.response ? 'Could not send your feedback. Please try again.' : 'You appear to be offline. Your message is still here — try again when you\'re connected.'));
    } finally { setSending(false); }
  };

  return (
    <div className="z-page z-page--narrow fb-page">
      <p className="fb-intro">Found a bug, or have an idea? The Zaptude team reads every message.</p>

      <form className="fb-form" onSubmit={submit} noValidate>
        <div className="z-field">
          <span className="z-label" id="fb-cat-label">What kind of feedback?</span>
          <div className="fb-cats" role="radiogroup" aria-labelledby="fb-cat-label">
            {CATEGORIES.map(c => (
              <button key={c.key} type="button" role="radio" aria-checked={category === c.key} className={`fb-cat${category === c.key ? ' fb-cat--active' : ''}`} onClick={() => setCategory(c.key)}>
                <Icon name={c.icon} size={20} />
                <span className="fb-cat-label">{c.label}</span>
              </button>
            ))}
          </div>
          <span className="z-help">{cat.hint}</span>
        </div>

        <div className="z-field">
          <label className="z-label" htmlFor="fb-message">Message</label>
          <textarea id="fb-message" className="z-textarea" value={message} maxLength={MAX} rows={6} placeholder={cat.placeholder} onChange={(e) => { setMessage(e.target.value); if (problem) setProblem(''); }} aria-describedby="fb-count" />
          <div className="z-counter" id="fb-count">{trimmed.length} / {MAX}</div>
        </div>

        {problem && <div className="z-error-text" role="alert">{problem}</div>}

        <button type="submit" className="z-btn z-btn--primary z-btn--block" disabled={!valid || sending}>
          <Icon name="send" size={18} /> {sending ? 'Sending…' : 'Send feedback'}
        </button>
      </form>

      <section className="z-section" aria-labelledby="fb-mine-title">
        <div className="z-section-head"><h2 className="z-section-title" id="fb-mine-title">Your recent feedback</h2></div>
        {mineError ? (
          <ErrorState error={mineError} onRetry={loadMine} compact />
        ) : mine === null ? (
          <Skeleton height={72} radius={16} />
        ) : mine.length === 0 ? (
          <EmptyState icon="feedback" art={false} compact tone="neutral" title="Nothing sent yet" body="Your feedback and its status will appear here." />
        ) : (
          <div className="fb-mine">
            {mine.map(f => {
              const s = STATUS[f.status] || STATUS.new;
              const c = CATEGORIES.find(x => x.key === f.category);
              return (
                <div key={f.id} className="z-surface z-surface--flat fb-item">
                  <div className="fb-item-top">
                    <span className="fb-item-cat"><Icon name={c?.icon || 'feedback'} size={16} /> {c?.label || 'Feedback'}</span>
                    <span className={`z-chip ${s.cls}`}>{s.label}</span>
                  </div>
                  <p className="fb-item-msg">{f.message}</p>
                  <span className="fb-item-when">{timeAgo(f.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
